// @ts-check
// Formulário dos campos obrigatórios para avançar (docs/arquitetura-appcrm/08-contrato-modelo-de-dados.md, seção 6):
// mostra exatamente o que o motor disse que falta (card, contato principal e cada aluno), grava, confere de novo e só
// então devolve a mudança de etapa. Cancelar deixa o card onde estava.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { createRecord, updateRecordOptimistically } from '../../data/record-store.js';
import { checkStageRequirements } from '../../data/stage-requirements.js';
import { getEmptyFieldValue, getRelationIdFieldName } from '../../lib/field-metadata.js';
import { toPhonesValue } from '../../lib/phone.js';
import { getRelationPreset } from '../../lib/relation-presets.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { enqueueSnackbar, showErrorSnackbar } from '../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { FormField, buildNestedPrefill } from '../side-panel/create-record-panel.js';
import { buildRelatedRecord, toLocalDateTimeInput } from './field-input.js';
import { toPickedRecord } from './record-picker.js';

const NEW_STUDENT_GROUP = 'new';
// "Criar" no seletor: pessoas e escolas nascem ali mesmo só com o nome; o resto da estrutura vem das Configurações
const QUICK_CREATE_OBJECTS = new Set(['person', 'company']);
// papel de quem nasce no formulário, para a ficha da pessoa já ficar certa
const PERSON_ROLE_BY_FIELD = /** @type {Record<string, string>} */ ({ pointOfContact: 'GUARDIAN', student: 'STUDENT' });

/**
 * @typedef {import('../../data/stage-requirements.js').MissingRequirement} MissingRequirement
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {{ id: string, label: string }} PickedRecord
 */

/** @param {string} objectName @param {string} fieldName */
const findField = (objectName, fieldName) => objectsByNameSingular.value.get(objectName)?.fields.find((field) => field.name === fieldName);

/** @param {MissingRequirement} item */
const groupOf = (item) => (item.opportunityStudentId === undefined ? item.scope : item.opportunityStudentId ?? NEW_STUDENT_GROUP);

/** @param {MissingRequirement} item */
const valueKey = (item) => `${groupOf(item)}:${item.fieldName}`;

/** @param {unknown} value */
const hasValue = (value) => {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'object' && 'digits' in /** @type {object} */ (value)) return Boolean(/** @type {any} */ (value).digits);
  return true;
};

/**
 * @param {{ objectMetadata: ObjectMetadata, record: any, nextStage: string, initialMissing: MissingRequirement[], includeVisitDate: boolean,
 *   onCancel: () => void, onDone: (extraPatch: Record<string, unknown>) => void }} props
 */
export function StageRequirementsForm({ objectMetadata, record, nextStage, initialMissing, includeVisitDate, onCancel, onDone }) {
  const [missing, setMissing] = useState(initialMissing);
  const [values, setValues] = useState(/** @type {Record<string, any>} */ ({}));
  const [visitDate, setVisitDate] = useState(record?.visitScheduledAt ? toLocalDateTimeInput(record.visitScheduledAt) : '');
  const [isSaving, setIsSaving] = useState(false);
  const [stillMissing, setStillMissing] = useState(/** @type {string[]} */ ([]));
  const stageField = objectMetadata.fields.find((field) => field.name === 'stage');
  const stageLabel = translateMetadataLabel(stageField?.options?.find((option) => option.value === nextStage)?.label ?? nextStage);
  const isContactMissing = missing.some((item) => item.key === 'POINT_OF_CONTACT');
  // contato escolhido no formulário pode já ter telefone e e-mail: o motor confere depois de gravar
  const isOptional = (/** @type {MissingRequirement} */ item) => item.scope === 'CONTACT' && isContactMissing;
  const setValue = (/** @type {string} */ key, /** @type {any} */ value) => setValues((current) => ({ ...current, [key]: value }));
  const isComplete = missing.every((item) => isOptional(item) || hasValue(values[valueKey(item)])) && (!includeVisitDate || Boolean(visitDate));

  /** @param {MissingRequirement} item @param {FieldMetadata} field */
  const quickCreateFor = (item, field) => {
    const targetName = field.relation?.targetObjectNameSingular ?? '';
    const targetObject = objectsByNameSingular.value.get(targetName);
    if (!targetObject || !QUICK_CREATE_OBJECTS.has(targetName)) return undefined;
    return async (/** @type {string} */ searchText) => {
      if (!searchText) {
        enqueueSnackbar({ message: t('stageRequirements.typeNameToCreate'), variant: 'error' });
        return;
      }
      const role = PERSON_ROLE_BY_FIELD[item.fieldName];
      try {
        const created = await createRecord(targetObject, {
          ...(getRelationPreset(field)?.prefill ?? {}),
          ...(targetName === 'person' && role ? { personRoles: [role] } : {}),
          ...buildNestedPrefill(targetObject, searchText, ''),
        });
        setValue(valueKey(item), toPickedRecord(created, targetName));
      } catch (error) {
        showErrorSnackbar(error);
      }
    };
  };

  /** @param {MissingRequirement} item */
  const renderField = (item) => {
    const field = findField(item.objectNameSingular, item.fieldName);
    if (!field) return null;
    const key = valueKey(item);
    return html`<div key=${key} class="stage-requirements-field">
      <${FormField} field=${{ ...field, isNullable: isOptional(item) }} value=${values[key]} onChange=${(value) => setValue(key, value)}
        onCreateNew=${field.type === 'RELATION' ? quickCreateFor(item, field) : undefined} />
      ${isOptional(item) ? html`<p class="input-hint">${t('stageRequirements.contactValueHint')}</p>` : null}
    </div>`;
  };

  const submit = async () => {
    setIsSaving(true);
    setStillMissing([]);
    try {
      await saveValues({ objectMetadata, record, missing, values });
      const remaining = await checkStageRequirements(record.id, nextStage);
      if (remaining.length > 0) {
        setMissing(remaining);
        setStillMissing(remaining.map((item) => t(`stageRequirements.rules.${item.key}.label`)));
        return;
      }
      onDone(includeVisitDate && visitDate ? { visitScheduledAt: new Date(visitDate).toISOString() } : {});
    } catch {
      // o snackbar da escrita já explicou; o formulário continua aberto com o que foi digitado
    } finally {
      setIsSaving(false);
    }
  };

  const cardItems = missing.filter((item) => item.scope === 'CARD');
  const contactItems = missing.filter((item) => item.scope === 'CONTACT');
  /** @type {Map<string, MissingRequirement[]>} */
  const studentGroups = new Map();
  for (const item of missing.filter((entry) => entry.opportunityStudentId !== undefined)) {
    studentGroups.set(groupOf(item), [...(studentGroups.get(groupOf(item)) ?? []), item]);
  }

  return html`<${Modal}
    title=${t('stageRequirements.dialogTitle')}
    subtitle=${t('stageRequirements.dialogSubtitle', { record: record?.name ?? '', stage: stageLabel })}
    confirmLabel=${t('stageRequirements.saveAndMove')}
    isConfirmDisabled=${!isComplete}
    isLoading=${isSaving}
    onClose=${onCancel}
    onConfirm=${submit}
  >
    <div class="stage-requirements-form">
      ${includeVisitDate
        ? html`<label class="stage-transition-field">
            <span>${t('stageRequirements.visitDate')}</span>
            <input class="text-input" type="datetime-local" required value=${visitDate}
              onInput=${(/** @type {Event} */ event) => setVisitDate(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>`
        : null}
      ${cardItems.length > 0
        ? html`<section class="stage-requirements-section"><h3>${t('stageRequirements.cardSection')}</h3>${cardItems.map(renderField)}</section>`
        : null}
      ${contactItems.length > 0
        ? html`<section class="stage-requirements-section"><h3>${t('stageRequirements.contactSection')}</h3>${contactItems.map(renderField)}</section>`
        : null}
      ${[...studentGroups].map(([groupId, items]) => html`<section key=${groupId} class="stage-requirements-section">
        <h3>${groupId === NEW_STUDENT_GROUP || !items[0].studentLabel ? t('stageRequirements.newStudent') : t('stageRequirements.studentSection', { name: items[0].studentLabel })}</h3>
        ${items.map(renderField)}
      </section>`)}
      ${stillMissing.length > 0
        ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} />
            <span>${t('stageRequirements.stillMissing', { fields: [...new Set(stillMissing)].join(', ') })}</span></div>`
        : null}
    </div>
  <//>`;
}

/**
 * Grava o que foi preenchido: campos do card (sem mudar a etapa), contato principal, alunos e escola de origem.
 * @param {{ objectMetadata: ObjectMetadata, record: any, missing: MissingRequirement[], values: Record<string, any> }} input
 */
async function saveValues({ objectMetadata, record, missing, values }) {
  const valueOf = (/** @type {MissingRequirement} */ item) => values[valueKey(item)];

  /** @type {Record<string, unknown>} */
  const cardPatch = {};
  /** @type {Record<string, unknown>} */
  const cardLocalPatch = {};
  for (const item of missing.filter((entry) => entry.scope === 'CARD')) {
    const field = findField(item.objectNameSingular, item.fieldName);
    const value = valueOf(item);
    if (!field || !hasValue(value)) continue;
    if (field.type === 'RELATION') {
      cardPatch[getRelationIdFieldName(field)] = value.id;
      cardLocalPatch[getRelationIdFieldName(field)] = value.id;
      cardLocalPatch[field.name] = buildRelatedRecord(objectsByNameSingular.value.get(field.relation?.targetObjectNameSingular ?? ''), value);
    } else {
      cardPatch[field.name] = value;
      cardLocalPatch[field.name] = value;
    }
  }
  if (Object.keys(cardPatch).length > 0) await updateRecordOptimistically(objectMetadata, record.id, cardPatch, cardLocalPatch);

  const personObject = objectsByNameSingular.value.get('person');
  const childObject = objectsByNameSingular.value.get('opportunityStudent');
  const chosenContactId = values[`CARD:pointOfContact`]?.id;
  /** @type {Record<string, unknown>} */
  const contactPatch = {};
  let contactId = /** @type {string | null} */ (null);
  for (const item of missing.filter((entry) => entry.scope === 'CONTACT')) {
    const field = findField(item.objectNameSingular, item.fieldName);
    const value = valueOf(item);
    contactId = item.recordId ?? chosenContactId ?? null;
    if (!field || !hasValue(value)) continue;
    if (field.type === 'PHONES') {
      contactPatch.phones = toPhonesValue(/** @type {Record<string, any>} */ (item.currentValue ?? getEmptyFieldValue(field)), value.countryCode, value.digits);
    } else if (field.type === 'EMAILS') {
      contactPatch.emails = { additionalEmails: [], ...(item.currentValue ?? {}), primaryEmail: String(value).trim() };
    }
  }
  if (personObject && contactId && Object.keys(contactPatch).length > 0) await updateRecordOptimistically(personObject, contactId, contactPatch);

  /** @type {Map<string, MissingRequirement[]>} */
  const groups = new Map();
  for (const item of missing.filter((entry) => entry.opportunityStudentId !== undefined)) groups.set(groupOf(item), [...(groups.get(groupOf(item)) ?? []), item]);
  for (const [groupId, items] of groups) {
    const studentPicked = values[`${groupId}:student`];
    const coursePicked = values[`${groupId}:course`];
    const schoolPicked = values[`${groupId}:originSchool`];
    const studentPersonId = studentPicked?.id ?? items.find((item) => item.fieldName === 'originSchool')?.recordId ?? null;
    if (childObject) {
      /** @type {Record<string, unknown>} */
      const childPatch = {};
      if (studentPicked?.id) childPatch.studentId = studentPicked.id;
      if (coursePicked?.id) childPatch.courseId = coursePicked.id;
      if (groupId === NEW_STUDENT_GROUP) {
        if (Object.keys(childPatch).length > 0) await createRecord(childObject, { ...childPatch, enrollmentOpportunityId: record.id });
      } else if (Object.keys(childPatch).length > 0) {
        await updateRecordOptimistically(childObject, groupId, childPatch);
      }
    }
    if (personObject && studentPersonId && schoolPicked?.id) await updateRecordOptimistically(personObject, studentPersonId, { originSchoolId: schoolPicked.id });
  }
}
